import { Palette, Video, PenTool, Target, BarChart3, Layers, Camera, Film, Megaphone, Lightbulb } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

// Fallback content (current hardcoded data)
const fallback = {
  seoTitle: "Performance Creative That Converts",
  seoDescription: "Creative services designed for performance marketing. Ad creative, video production, and brand design that drives measurable results.",
  canonicalUrl: "https://xperfomax.com/creative",
  category: "services",
  categoryLabel: "Services / Creative",
  headline: "Performance Creative That Converts",
  subheadline: "Creative should do more than look good. It should perform. We create assets designed to stop thumbs, capture attention, and drive action.",
  ctaText: "Discuss your creative needs",
  problemTitle: "Why Creative Underperforms",
  problems: [
    "Creative is optimized for brand approval, not performance.",
    "Testing frameworks are absent. Decisions are based on taste.",
    "Production cycles are too slow for campaign refresh needs.",
    "Creative strategy operates in isolation from media insights.",
  ],
  systemTitle: "Our Creative Approach",
  systemDescription: "Performance-led creative strategy with rapid iteration and testing.",
  services: [
    { name: "Brand Creative", icon: "Palette", components: ["Visual identity development", "Brand guidelines creation", "Design system implementation"], outcome: "Consistent, recognizable brand presence across all touchpoints." },
    { name: "Performance Creative", icon: "Target", components: ["Ad creative development", "Static & carousel ads", "Landing page graphics"], outcome: "Creative that drives clicks, not just impressions." },
    { name: "Video Production", icon: "Video", components: ["Video ad production", "Social video content", "Motion graphics"], outcome: "Scroll-stopping video that communicates and converts." },
    { name: "Content Design", icon: "PenTool", components: ["Social media graphics", "Email design", "Presentation design"], outcome: "On-brand content that maintains quality at scale." },
    { name: "Creative Testing", icon: "BarChart3", components: ["A/B creative testing", "Performance analysis", "Iterative optimization"], outcome: "Data-driven creative decisions that improve over time." },
    { name: "Creative Strategy", icon: "Layers", components: ["Campaign concepting", "Messaging frameworks", "Creative briefs"], outcome: "Strategic direction that aligns creative with business goals." },
  ],
  processTitle: "From concept to conversion.",
  processSteps: [
    { number: "01", title: "Brief & Discovery", description: "Understand your brand, audience, objectives, and what success looks like." },
    { number: "02", title: "Concept Development", description: "Develop creative concepts aligned with strategy and campaign goals." },
    { number: "03", title: "Design & Production", description: "Execute creative across required formats and platforms." },
    { number: "04", title: "Review & Refinement", description: "Collaborative feedback process to ensure alignment and quality." },
    { number: "05", title: "Delivery & Optimization", description: "Asset delivery with performance tracking and iterative improvements." },
  ],
  technologyCategories: [
    { title: "Design", icon: "Lightbulb", items: ["Figma", "Adobe Creative Cloud", "Canva"] },
    { title: "Video", icon: "Film", items: ["Premiere Pro", "After Effects", "DaVinci Resolve"] },
    { title: "Photography", icon: "Camera", items: ["Lightroom", "Capture One", "Studio Equipment"] },
    { title: "Collaboration", icon: "Megaphone", items: ["Frame.io", "Loom", "Notion"] },
  ],
  techTitle: "Creative Production Tools",
  techDescription: "Professional tools for design, video, and collaboration.",
  qualification: {
    forYou: [
      "You need performance-focused creative, not just pretty design",
      "You're running paid campaigns and need high-volume assets",
      "You want creative that's tested and optimized",
      "You have clear brand guidelines or need help creating them",
    ],
    notForYou: [
      "You need a full brand identity from scratch (we partner for this)",
      "You want one-off designs without strategic context",
      "You're not planning to measure creative performance",
      "You expect unlimited revisions without clear feedback",
    ],
  },
  faqs: [
    { question: "What's included in a creative package?", answer: "Packages vary by need. Typical deliverables include ad creatives in multiple sizes, social content, email graphics, and landing page assets. We scope based on your campaign requirements." },
    { question: "Do you handle video production in-house?", answer: "Yes. We have in-house video production capabilities for digital ads, social content, and motion graphics. For larger productions, we partner with specialized studios." },
    { question: "How do you ensure brand consistency?", answer: "We start by understanding or developing your brand guidelines. All creative is reviewed against these standards before delivery." },
    { question: "What's your typical turnaround time?", answer: "Standard creative requests take 5-7 business days. Rush delivery is available. Larger campaigns are scoped with dedicated timelines." },
    { question: "Do you offer ongoing creative support?", answer: "Yes. We offer retainer packages for ongoing creative needs including ad refreshes, social content, and campaign assets." },
  ],
  finalCtaTitle: "Creative that works as hard as your media.",
  finalCtaDescription: "Your campaigns deserve creative that converts. Let's build assets that perform.",
  finalCtaButtonText: "Discuss your creative needs",
};

const Creative = () => {
  const { content } = usePageContent("creative", fallback);

  // Resolve icon strings to components
  const services = content.services.map((s: any) => ({
    ...s,
    icon: resolveIcon(s.icon),
  }));

  const technologyCategories = content.technologyCategories.map((t: any) => ({
    ...t,
    icon: resolveIcon(t.icon),
  }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default Creative;
